import * as React from 'react'
import { FieldGroup, Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Notice } from '@/components/ui/notice'
import { SlideOver } from '@/components/ui/slide-over'
import { ErrorSummary, type ErrorSummaryItem } from '@/components/visitone/error-summary'
import { OperationFeedback } from '@/components/visitone/operation-feedback'
import { SaveBar, type SaveState } from '@/components/visitone/save-bar'
import { useRegisterUnsavedChanges, useUnsavedChanges } from '@/components/visitone/unsaved-changes'
import { useT } from '@/components/visitone/visitone-provider'
import { useAccess } from '@/hooks/use-access'
import { usePeopleMutations } from '@/hooks/use-people'
import { PeopleRequestError, type CustomRole, type RoleRow } from '@/lib/bff/people'
import type { GrantableAction } from '@/lib/people/grantable-actions'
import { ChoiceItem } from '@/components/visitone/surfaces/people/people-shared'
import { changedCount, errorCode, errorStatus } from '@/lib/people/people-text'

type FieldKey = 'name' | 'actions'
type Errors = Partial<Record<FieldKey, string>>
type Problem = 'roleLimit' | 'changedMeanwhile' | 'refused' | null

const FIELD_IDS: Record<FieldKey, string> = { name: 'role-name', actions: 'role-actions' }

/** What the editor is open for: a new role, or an existing custom role. */
export type RoleEditorTarget = { mode: 'create' } | { mode: 'edit'; role: RoleRow }

/*
 * Create or edit a custom role (VIS-332), saved with the panel's SaveBar
 * (VIS-321). Only grantable actions the caller holds are offered. Actions
 * the role already grants but the editor doesn't offer are kept as they are.
 */
export function RoleEditorPanel({
  target,
  grantable,
  existingNames,
  onClose,
  onSaved,
}: {
  target: RoleEditorTarget | null
  grantable: GrantableAction[]
  /** The tenant's role names, lower-cased, to refuse a duplicate before saving. */
  existingNames: Set<string>
  onClose: () => void
  onSaved: (role: CustomRole, created: boolean) => void
}) {
  const t = useT()
  const { can, tenantName: tenant } = useAccess()
  const { confirmThen } = useUnsavedChanges()
  const mutations = usePeopleMutations()
  const summaryRef = React.useRef<HTMLDivElement>(null)
  const open = target !== null
  const editing = target?.mode === 'edit' ? target.role : null

  const initialName = editing ? (editing.name ?? '') : ''
  const initialActions = React.useMemo(() => editing?.actions ?? [], [editing])
  const [name, setName] = React.useState(initialName)
  const [actions, setActions] = React.useState<string[]>(initialActions)
  const [errors, setErrors] = React.useState<Errors>({})
  const [summaryShown, setSummaryShown] = React.useState(false)
  const [problem, setProblem] = React.useState<Problem>(null)
  const [saveState, setSaveState] = React.useState<SaveState>('idle')
  const [shownFor, setShownFor] = React.useState(target)
  if (target !== shownFor) {
    setShownFor(target)
    setName(initialName)
    setActions(initialActions)
    setErrors({})
    setSummaryShown(false)
    setProblem(null)
    setSaveState('idle')
  }

  const offered = grantable.filter((action) => can(action.id))
  const areas = [...new Set(offered.map((action) => action.areaKey))]

  const count = (name.trim() === initialName.trim() ? 0 : 1) + changedCount(initialActions, actions)
  const discard = React.useCallback(() => {
    setName(initialName)
    setActions(initialActions)
    setErrors({})
    setSummaryShown(false)
    setProblem(null)
    setSaveState('idle')
  }, [initialName, initialActions])
  useRegisterUnsavedChanges(
    open
      ? {
          count,
          subject: t('people.editor.subject', { role: name.trim() || initialName || t('people.editor.newRole') }),
          discard,
        }
      : null,
  )

  const labels: Record<FieldKey, string> = { name: t('people.editor.name'), actions: t('people.editor.actions') }
  const summaryItems: ErrorSummaryItem[] = (Object.keys(FIELD_IDS) as FieldKey[]).flatMap((key) =>
    errors[key] ? [{ key, label: labels[key], message: errors[key] as string }] : [],
  )

  const showErrors = (next: Errors) => {
    setErrors(next)
    setSummaryShown(true)
    requestAnimationFrame(() => summaryRef.current?.focus())
  }

  const jumpTo = (key: string) => {
    const target =
      key === 'actions'
        ? (document.querySelector<HTMLElement>(`#${FIELD_IDS.actions} input`) ?? document.getElementById(FIELD_IDS.actions))
        : document.getElementById(FIELD_IDS.name)
    if (target && !target.matches('input')) target.setAttribute('tabindex', '-1')
    target?.focus()
  }

  const save = async () => {
    if (!target || saveState === 'saving') return
    const value = name.trim()
    const next: Errors = {}
    if (!value) next.name = t('people.editor.nameRequired')
    else if (value.toLowerCase() !== initialName.trim().toLowerCase() && existingNames.has(value.toLowerCase())) {
      next.name = t('people.editor.nameTaken', { name: value, tenant })
    }
    if (actions.length === 0) next.actions = t('people.editor.actionsRequired')
    if (Object.keys(next).length > 0) return showErrors(next)

    setProblem(null)
    setSaveState('saving')
    try {
      const saved = editing
        ? await mutations.updateRole(editing.id, { name: value, actions })
        : await mutations.createRole({ name: value, actions })
      setSaveState('idle')
      onSaved(saved, !editing)
    } catch (error) {
      setSaveState('idle')
      const code = errorCode(error)
      if (code === 'role_name_taken') showErrors({ name: t('people.editor.nameTaken', { name: value, tenant }) })
      else if (code === 'role_limit') setProblem('roleLimit')
      else if (code === 'beyond_grant_limit') showErrors({ actions: t('people.editor.actionsUnavailable') })
      else if (error instanceof PeopleRequestError && error.problems.length > 0) showErrors(problemErrors(error, t))
      else if (errorStatus(error) === 403) setProblem('refused')
      else if (errorStatus(error) === 404 || errorStatus(error) === 409) {
        setProblem('changedMeanwhile')
        void mutations.revalidate()
      } else setSaveState('failed')
    }
  }

  const toggle = (actionId: string, next: boolean) => {
    setActions((current) => (next ? [...current, actionId] : current.filter((id) => id !== actionId)))
    setErrors((current) => ({ ...current, actions: undefined }))
    if (saveState === 'failed') setSaveState('idle')
  }

  return (
    <SlideOver
      open={open}
      title={editing ? t('people.editor.editTitle', { role: initialName }) : t('people.editor.createTitle')}
      onClose={onClose}
      footer={
        <SaveBar
          docked="panel"
          count={count}
          invalidCount={summaryShown ? summaryItems.length : 0}
          state={saveState}
          onDiscard={() => confirmThen({ kind: 'discard' }, discard)}
          onSave={() => void save()}
        />
      }
    >
      {open ? (
        <form
          noValidate
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault()
            void save()
          }}
        >
          {editing ? <Notice tone="info" title={t('people.editor.editNote', { count: editing.holderCount })} /> : null}
          {summaryShown && summaryItems.length > 0 ? (
            <ErrorSummary ref={summaryRef} errors={summaryItems} onJump={jumpTo} />
          ) : null}
          {problem === 'roleLimit' ? (
            <OperationFeedback state="error" title={t('people.editor.roleLimit', { tenant })} />
          ) : null}
          {problem === 'changedMeanwhile' ? (
            <OperationFeedback state="error" title={t('people.editor.changedMeanwhile')} />
          ) : null}
          {problem === 'refused' ? (
            <OperationFeedback
              state="error"
              title={t('core.access.actionRefusedTitle')}
              detail={t('core.access.actionRefusedBody', { tenant })}
            />
          ) : null}
          <Field id={FIELD_IDS.name} label={labels.name} help={t('people.editor.nameHelp')} required error={errors.name}>
            <Input
              autoComplete="off"
              aria-required="true"
              value={name}
              onChange={(event) => {
                setName(event.target.value)
                setErrors((current) => ({ ...current, name: undefined }))
                if (saveState === 'failed') setSaveState('idle')
              }}
            />
          </Field>
          <FieldGroup
            id={FIELD_IDS.actions}
            legend={labels.actions}
            help={t('people.editor.actionsHelp')}
            error={errors.actions}
            required
          >
            {areas.map((area) => (
              <div key={area} className="mt-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-fg-muted">{t(area)}</p>
                <ul className="mt-1 flex flex-col">
                  {offered
                    .filter((action) => action.areaKey === area)
                    .map((action) => (
                      <ChoiceItem
                        key={action.id}
                        id={`role-action-${action.id}`}
                        label={t(action.labelKey)}
                        checked={actions.includes(action.id)}
                        onChange={(next) => toggle(action.id, next)}
                      />
                    ))}
                </ul>
              </div>
            ))}
          </FieldGroup>
        </form>
      ) : null}
    </SlideOver>
  )
}

function problemErrors(error: PeopleRequestError, t: ReturnType<typeof useT>): Errors {
  const next: Errors = {}
  for (const problem of error.problems) {
    if (problem.field === 'name') next.name = t('people.editor.nameRequired')
    if (problem.field === 'actions') {
      next.actions = problem.code === 'required' ? t('people.editor.actionsRequired') : t('people.editor.actionsUnavailable')
    }
  }
  return next
}
